import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/api";
import { getQueryKey } from "@key.ai/constants";
import { cleanObject } from "@/lib/helper";
import eventClientApi from "@/api-client/event-client";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import { isEpochInPast } from "@/lib/utils";

interface ApiResponsePagination<T> {
  data: T[];
  count: number;
  pageParam: number;
}
export const useCalendarEvents = ({
  calendarId = "",
  tag = "",
  current_epoc
}: {
  calendarId: string;
  tag: string;
  current_epoc: number;
}) => {
  const queryKey = getQueryKey("CALENDAR_EVENTS", {
    calendarId: calendarId
  });

  const { status: authStatus } = useSession();
  const fetchCalendars = async ({
    pageParam = 1
  }: {
    pageParam: unknown;
  }): Promise<ApiResponsePagination<any>> => {
    try {
      const { data } = await eventClientApi
        .get(
          `api/v1${authStatus === "authenticated" ? "" : "/public"}/calendars/${calendarId}/events`,
          {
            searchParams: cleanObject({
              page: pageParam as number,
              limit: 10,
              currentEpoch: current_epoc,
              type: isEpochInPast(current_epoc) ? "past" : ""
              // sort: "start_epoc:ASC",
              // tag: tag
            })
          }
        )
        .json<ApiResponse<any>>();

      return { data: data?.data, count: data?.count, pageParam: pageParam as number };
    } catch (error) {
      console.log("Error fetching calendar events:", error);
      toast.error("Error fetching calendar events");
      return { data: [], count: 0, pageParam: pageParam as number };
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    refetch,
    isFetching
  } = useInfiniteQuery<ApiResponsePagination<any>>({
    queryKey: [queryKey, calendarId, tag, current_epoc],
    queryFn: fetchCalendars,
    getNextPageParam: (lastPage: any) => {
      if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
        return lastPage?.pageParam + 1;
      }

      return undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    gcTime: 0,
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !!calendarId
  });

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetching,
    refetch
  };
};
